/**
 * Analytic collision queries against level primitives.
 *
 * Two operations, both of which every consumer (pogo tip sweep, body
 * depenetration, camera occlusion) is built on:
 *
 * - {@link closestPoint} — nearest point on a primitive's surface, with a signed
 *   distance (negative when the query point is *inside*) and an outward normal.
 * - {@link sweepSphere} — first time of impact for a sphere travelling along a
 *   segment.
 *
 * Client-only: three.js `Vector3`/`Quaternion` are used for the math. The
 * primitive data itself comes from `@shared/primitives` and is engine-agnostic.
 *
 * Conventions used everywhere in this file:
 * - `normal` points **outward** from the primitive, i.e. the direction you
 *   depenetrate along.
 * - `point` is on the primitive's surface.
 * - `dist` is signed: `> 0` outside, `< 0` inside (magnitude = penetration).
 * - Sweep `t` is normalised to `[0, 1]` along `from → to`.
 */

import { Quaternion, Vector3 } from 'three';
import type {
  BoxPrim,
  CapsulePrim,
  CylinderPrim,
  Primitive,
  SpherePrim,
  V3,
} from '@shared/primitives';

/** Result of a {@link closestPoint} query. */
export interface ContactHit {
  /** Closest point on the primitive's surface, in world space. */
  point: Vector3;
  /** Unit outward normal at `point` (the direction that separates the query point). */
  normal: Vector3;
  /** Signed distance from the query point to the surface; negative when inside. */
  dist: number;
  /** The primitive that was queried. */
  prim: Primitive;
}

/** Result of a {@link sweepSphere} query. */
export interface SweepHit {
  /** Fraction along `from → to` at which contact occurs, in `[0, 1]`. */
  t: number;
  /** Contact point on the primitive's surface, in world space. */
  point: Vector3;
  /** Unit outward normal at the contact point. */
  normal: Vector3;
  /** The primitive that was hit. */
  prim: Primitive;
}

const EPS = 1e-9;
/** Surface tolerance for accepting a sweep solution, in metres. */
const SURFACE_TOL = 1e-3;
/** Iteration cap for the conservative-advancement fallback. */
const ADVANCE_STEPS = 64;

// --- module scratch (this module is single-threaded and never re-enters) ----
const _q = new Quaternion();
const _qi = new Quaternion();
const _local = new Vector3();
const _diff = new Vector3();
const _axis = new Vector3();
const _rel = new Vector3();
const _radial = new Vector3();
const _onAxis = new Vector3();
const _dir = new Vector3();
const _localDir = new Vector3();
const _probe = new Vector3();
const _dPerp = new Vector3();
const _wPerp = new Vector3();
const _capHit = new Vector3();

const _hitA: ContactHit = makeContact();
const _hitB: ContactHit = makeContact();

function makeContact(): ContactHit {
  return {
    point: new Vector3(),
    normal: new Vector3(0, 1, 0),
    dist: Infinity,
    // Placeholder; every write path overwrites this before returning.
    prim: undefined as unknown as Primitive,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function setV3(out: Vector3, v: V3): Vector3 {
  return out.set(v[0], v[1], v[2]);
}

// ---------------------------------------------------------------------------
// closestPoint
// ---------------------------------------------------------------------------

/**
 * Nearest point on `prim`'s surface to `p`.
 *
 * @param out optional result object to write into, to avoid an allocation.
 *   The returned value is `out` when supplied. Do not pass the same object you
 *   are still reading from.
 */
export function closestPoint(prim: Primitive, p: Vector3, out?: ContactHit): ContactHit {
  const hit = out ?? makeContact();
  switch (prim.kind) {
    case 'box':
      return boxClosest(prim, p, hit);
    case 'sphere':
      return sphereClosest(prim, p, hit);
    case 'cylinder':
      return cylinderClosest(prim, p, hit);
    case 'capsule':
      return capsuleClosest(prim, p, hit);
  }
}

function boxClosest(prim: BoxPrim, p: Vector3, hit: ContactHit): ContactHit {
  _q.set(prim.quat[0], prim.quat[1], prim.quat[2], prim.quat[3]);
  _qi.copy(_q).invert();
  _local.set(p.x - prim.center[0], p.y - prim.center[1], p.z - prim.center[2]);
  _local.applyQuaternion(_qi);

  const hx = prim.half[0];
  const hy = prim.half[1];
  const hz = prim.half[2];
  const cx = clamp(_local.x, -hx, hx);
  const cy = clamp(_local.y, -hy, hy);
  const cz = clamp(_local.z, -hz, hz);

  if (cx !== _local.x || cy !== _local.y || cz !== _local.z) {
    // Outside: the clamped point is the exact closest point on the surface.
    _diff.set(_local.x - cx, _local.y - cy, _local.z - cz);
    const d = _diff.length();
    hit.dist = d;
    hit.normal.copy(_diff).multiplyScalar(1 / d).applyQuaternion(_q);
    hit.point.set(cx, cy, cz).applyQuaternion(_q);
    hit.point.x += prim.center[0];
    hit.point.y += prim.center[1];
    hit.point.z += prim.center[2];
  } else {
    // Inside: push out through the nearest face.
    const px = hx - Math.abs(_local.x);
    const py = hy - Math.abs(_local.y);
    const pz = hz - Math.abs(_local.z);
    let pen: number;
    if (px <= py && px <= pz) {
      pen = px;
      const s = _local.x < 0 ? -1 : 1;
      hit.normal.set(s, 0, 0);
      hit.point.set(s * hx, _local.y, _local.z);
    } else if (py <= pz) {
      pen = py;
      const s = _local.y < 0 ? -1 : 1;
      hit.normal.set(0, s, 0);
      hit.point.set(_local.x, s * hy, _local.z);
    } else {
      pen = pz;
      const s = _local.z < 0 ? -1 : 1;
      hit.normal.set(0, 0, s);
      hit.point.set(_local.x, _local.y, s * hz);
    }
    hit.dist = -pen;
    hit.normal.applyQuaternion(_q);
    hit.point.applyQuaternion(_q);
    hit.point.x += prim.center[0];
    hit.point.y += prim.center[1];
    hit.point.z += prim.center[2];
  }
  hit.prim = prim;
  return hit;
}

function sphereClosest(prim: SpherePrim, p: Vector3, hit: ContactHit): ContactHit {
  _diff.set(p.x - prim.center[0], p.y - prim.center[1], p.z - prim.center[2]);
  const len = _diff.length();
  if (len < EPS) {
    // Dead centre — any direction is equally valid; pick up.
    hit.normal.set(0, 1, 0);
    hit.dist = -prim.radius;
  } else {
    hit.normal.copy(_diff).multiplyScalar(1 / len);
    hit.dist = len - prim.radius;
  }
  hit.point.copy(hit.normal).multiplyScalar(prim.radius);
  hit.point.x += prim.center[0];
  hit.point.y += prim.center[1];
  hit.point.z += prim.center[2];
  hit.prim = prim;
  return hit;
}

/**
 * Capped cylinder. The flat caps matter: landing on top of a pillar must produce
 * a normal along the axis, not a radial one, or the pogo tip skates off the rim.
 */
function cylinderClosest(prim: CylinderPrim, p: Vector3, hit: ContactHit): ContactHit {
  setV3(_onAxis, prim.a);
  setV3(_axis, prim.b).sub(_onAxis);
  const h = _axis.length();
  const R = prim.radius;
  if (h < EPS) {
    // Degenerate cylinder — treat as a sphere so callers still get a sane normal.
    return sphereClosest({ kind: 'sphere', center: prim.a, radius: R, material: prim.material }, p, hit);
  }
  _axis.multiplyScalar(1 / h); // unit axis, a → b

  _rel.copy(p).sub(_onAxis); // p - a
  const along = _rel.dot(_axis);
  _radial.copy(_rel).addScaledVector(_axis, -along);
  const r = _radial.length();

  const insideSpan = along >= 0 && along <= h;
  const insideRadius = r <= R;

  if (insideSpan && insideRadius) {
    // Fully inside: leave via the shallowest of {side, bottom cap, top cap}.
    const penSide = R - r;
    const penBottom = along;
    const penTop = h - along;
    if (penSide <= penBottom && penSide <= penTop && r > EPS) {
      hit.normal.copy(_radial).multiplyScalar(1 / r);
      hit.dist = -penSide;
      hit.point.copy(_onAxis).addScaledVector(_axis, along).addScaledVector(hit.normal, R);
    } else if (penBottom <= penTop) {
      hit.normal.copy(_axis).multiplyScalar(-1);
      hit.dist = -penBottom;
      hit.point.copy(_onAxis).add(_radial);
    } else {
      hit.normal.copy(_axis);
      hit.dist = -penTop;
      hit.point.copy(_onAxis).addScaledVector(_axis, h).add(_radial);
    }
  } else if (insideSpan) {
    // Beside the shaft: closest point is on the curved side.
    hit.normal.copy(_radial).multiplyScalar(1 / r);
    hit.dist = r - R;
    hit.point.copy(_onAxis).addScaledVector(_axis, along).addScaledVector(hit.normal, R);
  } else if (insideRadius) {
    // Directly above or below a cap: flat contact, normal along the axis.
    const top = along > h;
    hit.normal.copy(_axis);
    if (!top) hit.normal.multiplyScalar(-1);
    hit.dist = top ? along - h : -along;
    hit.point.copy(_onAxis).addScaledVector(_axis, top ? h : 0).add(_radial);
  } else {
    // Off the end and off to the side: closest point is on a cap rim circle.
    const capAlong = along > h ? h : 0;
    hit.point
      .copy(_onAxis)
      .addScaledVector(_axis, capAlong)
      .addScaledVector(_radial, R / r);
    _diff.copy(p).sub(hit.point);
    const d = _diff.length();
    hit.dist = d;
    if (d < EPS) hit.normal.copy(_radial).multiplyScalar(1 / r);
    else hit.normal.copy(_diff).multiplyScalar(1 / d);
  }
  hit.prim = prim;
  return hit;
}

/** Capsule: exact, via the closest point on the core segment. */
function capsuleClosest(prim: CapsulePrim, p: Vector3, hit: ContactHit): ContactHit {
  setV3(_onAxis, prim.a);
  setV3(_axis, prim.b).sub(_onAxis);
  const hh = _axis.lengthSq();
  const s = hh < EPS ? 0 : clamp(_rel.copy(p).sub(_onAxis).dot(_axis) / hh, 0, 1);
  _onAxis.addScaledVector(_axis, s); // closest point on segment

  _diff.copy(p).sub(_onAxis);
  const len = _diff.length();
  if (len < EPS) {
    hit.normal.set(0, 1, 0);
    hit.dist = -prim.radius;
  } else {
    hit.normal.copy(_diff).multiplyScalar(1 / len);
    hit.dist = len - prim.radius;
  }
  hit.point.copy(_onAxis).addScaledVector(hit.normal, prim.radius);
  hit.prim = prim;
  return hit;
}

// ---------------------------------------------------------------------------
// sweepSphere
// ---------------------------------------------------------------------------

/**
 * Sweep a sphere of `radius` from `from` to `to` and return the earliest contact.
 *
 * Returns `null` when the sphere never touches `prim` over the segment.
 *
 * If the sphere is **already overlapping** at `from`, the result is `t = 0` with
 * the depenetration normal, so callers can always resolve rather than tunnel.
 *
 * Implementation: an analytic first-hit per primitive kind (slab test for boxes,
 * quadratics for the round kinds), validated against the exact distance field.
 * The analytic forms are slightly generous at box corners and cylinder rims —
 * where validation fails, a conservative-advancement fallback finds the true
 * time of impact. The distance functions are 1-Lipschitz, so the fallback can
 * never step through a surface.
 */
export function sweepSphere(
  prim: Primitive,
  from: Vector3,
  to: Vector3,
  radius: number,
): SweepHit | null {
  const start = closestPoint(prim, from, _hitA);
  if (start.dist <= radius) {
    return {
      t: 0,
      point: start.point.clone(),
      normal: start.normal.clone(),
      prim,
    };
  }

  _dir.copy(to).sub(from);
  const segLen = _dir.length();
  if (segLen < EPS) return null;
  _dir.multiplyScalar(1 / segLen);

  // Exact early-out: the distance field is 1-Lipschitz, so the sphere cannot
  // close a gap larger than the distance it travels.
  const gap0 = start.dist - radius;
  if (gap0 > segLen) return null;

  let d = analyticSweep(prim, from, _dir, segLen, radius);
  if (d < 0 || !onSurface(prim, from, d, radius)) {
    d = advanceSweep(prim, from, segLen, radius, gap0);
    if (d < 0) return null;
  }

  _probe.copy(from).addScaledVector(_dir, d);
  const contact = closestPoint(prim, _probe, _hitB);
  return {
    t: clamp(d / segLen, 0, 1),
    point: contact.point.clone(),
    normal: contact.normal.clone(),
    prim,
  };
}

/** True when the swept sphere at distance `d` is touching the surface. */
function onSurface(prim: Primitive, from: Vector3, d: number, radius: number): boolean {
  if (d < 0) return false;
  _probe.copy(from).addScaledVector(_dir, d);
  return closestPoint(prim, _probe, _hitB).dist <= radius + SURFACE_TOL;
}

/**
 * Conservative advancement: repeatedly step forward by the current clearance.
 * Guaranteed not to skip a hit; converges quickly except on grazing paths, which
 * are correctly reported as misses when they never close the gap.
 */
function advanceSweep(
  prim: Primitive,
  from: Vector3,
  segLen: number,
  radius: number,
  gap0: number,
): number {
  let d = 0;
  let gap = gap0;
  for (let i = 0; i < ADVANCE_STEPS; i++) {
    if (gap <= SURFACE_TOL) return d;
    d += gap;
    if (d > segLen) return -1;
    _probe.copy(from).addScaledVector(_dir, d);
    gap = closestPoint(prim, _probe, _hitB).dist - radius;
  }
  return gap <= SURFACE_TOL * 10 && d <= segLen ? d : -1;
}

/** Analytic first-hit distance along `dir`, or `-1` if there is no candidate. */
function analyticSweep(
  prim: Primitive,
  from: Vector3,
  dir: Vector3,
  segLen: number,
  radius: number,
): number {
  switch (prim.kind) {
    case 'box':
      return boxSweep(prim, from, dir, segLen, radius);
    case 'sphere':
      return raySphere(from, dir, segLen, prim.center[0], prim.center[1], prim.center[2], prim.radius + radius);
    case 'cylinder':
      return cylinderSweep(prim, from, dir, segLen, radius);
    case 'capsule':
      return capsuleSweep(prim, from, dir, segLen, radius);
  }
}

/** Ray vs sphere; returns the near root within `[0, segLen]`, else `-1`. */
function raySphere(
  from: Vector3,
  dir: Vector3,
  segLen: number,
  cx: number,
  cy: number,
  cz: number,
  rr: number,
): number {
  const ox = from.x - cx;
  const oy = from.y - cy;
  const oz = from.z - cz;
  const b = ox * dir.x + oy * dir.y + oz * dir.z;
  const c = ox * ox + oy * oy + oz * oz - rr * rr;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const sq = Math.sqrt(disc);
  let t = -b - sq;
  if (t < 0) t = -b + sq;
  if (t < 0 || t > segLen) return -1;
  return t;
}

/** Segment vs the box's local AABB expanded by `radius` (slab test). */
function boxSweep(
  prim: BoxPrim,
  from: Vector3,
  dir: Vector3,
  segLen: number,
  radius: number,
): number {
  _q.set(prim.quat[0], prim.quat[1], prim.quat[2], prim.quat[3]);
  _qi.copy(_q).invert();
  _local.set(from.x - prim.center[0], from.y - prim.center[1], from.z - prim.center[2]);
  _local.applyQuaternion(_qi);
  _localDir.copy(dir).applyQuaternion(_qi);

  let tmin = 0;
  let tmax = segLen;
  for (let i = 0; i < 3; i++) {
    const o = i === 0 ? _local.x : i === 1 ? _local.y : _local.z;
    const dv = i === 0 ? _localDir.x : i === 1 ? _localDir.y : _localDir.z;
    const e = (prim.half[i] ?? 0) + radius;
    if (Math.abs(dv) < 1e-8) {
      if (o < -e || o > e) return -1;
      continue;
    }
    let t1 = (-e - o) / dv;
    let t2 = (e - o) / dv;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
    }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  return tmin;
}

/**
 * Capped cylinder: the earliest of the side surface (infinite cylinder of
 * `R + radius`, clamped to the axial span) and the two cap planes offset
 * outward by `radius`.
 */
function cylinderSweep(
  prim: CylinderPrim,
  from: Vector3,
  dir: Vector3,
  segLen: number,
  radius: number,
): number {
  setV3(_onAxis, prim.a);
  setV3(_axis, prim.b).sub(_onAxis);
  const h = _axis.length();
  if (h < EPS) {
    return raySphere(from, dir, segLen, prim.a[0], prim.a[1], prim.a[2], prim.radius + radius);
  }
  _axis.multiplyScalar(1 / h);

  const R = prim.radius;
  const rr = R + radius;
  _rel.copy(from).sub(_onAxis);
  const relAlong = _rel.dot(_axis);
  const dirAlong = dir.dot(_axis);

  let best = -1;

  // --- side surface ---
  _wPerp.copy(_rel).addScaledVector(_axis, -relAlong);
  _dPerp.copy(dir).addScaledVector(_axis, -dirAlong);
  const A = _dPerp.lengthSq();
  if (A > 1e-12) {
    const B = 2 * _wPerp.dot(_dPerp);
    const C = _wPerp.lengthSq() - rr * rr;
    const disc = B * B - 4 * A * C;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      const inv = 1 / (2 * A);
      for (let k = 0; k < 2; k++) {
        const t = (k === 0 ? -B - sq : -B + sq) * inv;
        if (t < 0 || t > segLen) continue;
        const axialAt = relAlong + dirAlong * t;
        if (axialAt < 0 || axialAt > h) continue;
        best = t;
        break;
      }
    }
  }

  // --- cap discs, on planes pushed out by the sphere radius ---
  if (Math.abs(dirAlong) > 1e-8) {
    for (let k = 0; k < 2; k++) {
      const planeAlong = k === 0 ? -radius : h + radius;
      const t = (planeAlong - relAlong) / dirAlong;
      if (t < 0 || t > segLen) continue;
      if (best >= 0 && t >= best) continue;
      _capHit.copy(_rel).addScaledVector(dir, t);
      const a2 = _capHit.dot(_axis);
      _capHit.addScaledVector(_axis, -a2);
      if (_capHit.lengthSq() <= R * R) best = t;
    }
  }

  return best;
}

/** Capsule: ray vs a capsule of `R + radius` — side span plus the two end spheres. */
function capsuleSweep(
  prim: CapsulePrim,
  from: Vector3,
  dir: Vector3,
  segLen: number,
  radius: number,
): number {
  setV3(_onAxis, prim.a);
  setV3(_axis, prim.b).sub(_onAxis);
  const h = _axis.length();
  const rr = prim.radius + radius;
  if (h < EPS) {
    return raySphere(from, dir, segLen, prim.a[0], prim.a[1], prim.a[2], rr);
  }
  _axis.multiplyScalar(1 / h);

  let best = -1;

  _rel.copy(from).sub(_onAxis);
  const relAlong = _rel.dot(_axis);
  const dirAlong = dir.dot(_axis);
  _wPerp.copy(_rel).addScaledVector(_axis, -relAlong);
  _dPerp.copy(dir).addScaledVector(_axis, -dirAlong);
  const A = _dPerp.lengthSq();
  if (A > 1e-12) {
    const B = 2 * _wPerp.dot(_dPerp);
    const C = _wPerp.lengthSq() - rr * rr;
    const disc = B * B - 4 * A * C;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      const inv = 1 / (2 * A);
      for (let k = 0; k < 2; k++) {
        const t = (k === 0 ? -B - sq : -B + sq) * inv;
        if (t < 0 || t > segLen) continue;
        const axialAt = relAlong + dirAlong * t;
        if (axialAt < 0 || axialAt > h) continue;
        best = t;
        break;
      }
    }
  }

  for (let k = 0; k < 2; k++) {
    const end = k === 0 ? prim.a : prim.b;
    const t = raySphere(from, dir, segLen, end[0], end[1], end[2], rr);
    if (t >= 0 && (best < 0 || t < best)) best = t;
  }

  return best;
}
